import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Search, Plus, ImageIcon, Film, Loader2, ExternalLink, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AdminMediaGallery = () => {
  const [universes, setUniverses] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("image");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [importedIds, setImportedIds] = useState(new Set());
  const [selectedUniverse, setSelectedUniverse] = useState("");
  
  // Manual add form
  const [manualUrl, setManualUrl] = useState("");
  const [manualTitle, setManualTitle] = useState("");
  const [manualDesc, setManualDesc] = useState("");
  const [manualType, setManualType] = useState("image");
  const [manualUniverse, setManualUniverse] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [uniRes, galleryRes] = await Promise.all([
        axios.get(`${API}/universes`),
        axios.get(`${API}/gallery`),
      ]);
      setUniverses(uniRes.data);
      setGalleryItems(galleryRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Enter a search query");
      return;
    }
    setSearching(true);
    try {
      const response = await axios.post(`${API}/media/search`, {
        query: searchQuery,
        media_type: searchType,
        per_page: 12,
      });
      setSearchResults(response.data.results);
      if (response.data.results.length === 0) {
        toast.info("No results found. Try different keywords.");
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleImport = async (item) => {
    if (!selectedUniverse) {
      toast.error("Select a universe first to import media into");
      return;
    }
    try {
      await axios.post(`${API}/gallery/import`, {
        universe_id: selectedUniverse,
        title: item.title,
        description: item.description || `Imported from ${item.source}`,
        media_type: item.media_type,
        url: item.url,
        source: item.source,
      });
      setImportedIds(prev => new Set([...prev, item.id]));
      toast.success(`Imported "${item.title}" to gallery!`);
      fetchData();
    } catch (error) {
      toast.error("Import failed");
    }
  };

  const handleManualAdd = async () => {
    if (!manualUrl || !manualTitle || !manualUniverse) {
      toast.error("URL, title, and universe are required");
      return;
    }
    try {
      await axios.post(`${API}/gallery/import`, {
        universe_id: manualUniverse,
        title: manualTitle,
        description: manualDesc,
        media_type: manualType,
        url: manualUrl,
        source: "Manual",
      });
      toast.success("Added to gallery!");
      setManualUrl("");
      setManualTitle("");
      setManualDesc("");
      fetchData();
    } catch (error) {
      toast.error("Failed to add");
    }
  };

  const handleDeleteGalleryItem = async (itemId) => {
    try {
      await axios.delete(`${API}/gallery/${itemId}`);
      toast.success("Removed from gallery");
      setGalleryItems(prev => prev.filter(i => i.id !== itemId));
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <AdminLayout title="Media Gallery Manager">
      <Tabs defaultValue="discover" className="w-full">
        <TabsList className="flex gap-2 bg-transparent border-b border-white/10 pb-3 mb-6">
          <TabsTrigger value="discover" className="flex items-center gap-2 px-4 py-2 text-sm data-[state=active]:text-yellow-500 data-[state=active]:border-b-2 data-[state=active]:border-yellow-500 text-slate-400 bg-transparent" data-testid="tab-discover">
            <Search className="w-4 h-4" /> Discover Media
          </TabsTrigger>
          <TabsTrigger value="manage" className="flex items-center gap-2 px-4 py-2 text-sm data-[state=active]:text-yellow-500 data-[state=active]:border-b-2 data-[state=active]:border-yellow-500 text-slate-400 bg-transparent" data-testid="tab-manage">
            <ImageIcon className="w-4 h-4" /> Current Gallery ({galleryItems.length})
          </TabsTrigger>
          <TabsTrigger value="manual" className="flex items-center gap-2 px-4 py-2 text-sm data-[state=active]:text-yellow-500 data-[state=active]:border-b-2 data-[state=active]:border-yellow-500 text-slate-400 bg-transparent" data-testid="tab-manual">
            <Plus className="w-4 h-4" /> Add by URL
          </TabsTrigger>
        </TabsList>

        {/* Discover Tab */}
        <TabsContent value="discover">
          <div className="card-surface p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded bg-sky-500/20 flex items-center justify-center">
                <Search className="w-5 h-5 text-sky-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white font-['Outfit']">Discover External Media</h2>
                <p className="text-sm text-slate-400">Search for images and videos to add to universe galleries</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div className="md:col-span-2">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search: star wars, fantasy castle, marvel..."
                  className="input-dark"
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  data-testid="media-search-input"
                />
              </div>
              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger className="input-dark" data-testid="media-search-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-white/10">
                  <SelectItem value="image">Images</SelectItem>
                  <SelectItem value="video">Videos</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} disabled={searching} className="btn-primary" data-testid="media-search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                Search
              </Button>
            </div>

            {/* Universe selector for import target */}
            <div className="flex items-center gap-3">
              <Label className="text-slate-400 text-sm whitespace-nowrap">Import to:</Label>
              <Select value={selectedUniverse} onValueChange={setSelectedUniverse}>
                <SelectTrigger className="input-dark max-w-xs" data-testid="import-universe">
                  <SelectValue placeholder="Select target universe" />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-white/10">
                  {universes.map((uni) => (
                    <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {searchResults.map((item) => (
                <div key={item.id} className="card-surface overflow-hidden group" data-testid={`search-result-${item.id}`}>
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={item.thumbnail || item.url}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    {item.media_type === "video" && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <Film className="w-8 h-8 text-white/80" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-white text-sm font-medium truncate">{item.title}</p>
                    <p className="text-slate-500 text-xs mt-1">via {item.source}</p>
                    <Button
                      size="sm"
                      onClick={() => handleImport(item)}
                      disabled={importedIds.has(item.id)}
                      className={`mt-2 w-full text-xs ${importedIds.has(item.id) ? 'bg-green-600' : 'btn-primary'}`}
                      data-testid={`import-btn-${item.id}`}
                    >
                      {importedIds.has(item.id) ? (
                        <><Check className="w-3 h-3 mr-1" /> Imported</>
                      ) : (
                        <><Plus className="w-3 h-3 mr-1" /> Add to Gallery</>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Manage Tab */}
        <TabsContent value="manage">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {galleryItems.map((item) => (
              <div key={item.id} className="card-surface overflow-hidden" data-testid={`gallery-manage-${item.id}`}>
                <div className="relative aspect-video overflow-hidden">
                  {item.media_type === "video" && item.url.includes("youtube") ? (
                    <img
                      src={`https://img.youtube.com/vi/${getYouTubeId(item.url)}/hqdefault.jpg`}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img src={item.url} alt={item.title} className="w-full h-full object-cover" />
                  )}
                  <span className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded ${
                    item.media_type === "video" ? "bg-red-500/80" : "bg-sky-500/80"
                  } text-white`}>
                    {item.media_type}
                  </span>
                </div>
                <div className="p-3">
                  <p className="text-white text-sm font-medium truncate">{item.title}</p>
                  <p className="text-slate-500 text-xs truncate">{item.source || "Uploaded"}</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteGalleryItem(item.id)}
                    className="mt-2 w-full text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`delete-gallery-${item.id}`}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
            {galleryItems.length === 0 && (
              <div className="col-span-full text-center py-12">
                <ImageIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500">No gallery items yet. Use Discover to add media.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Manual Add Tab */}
        <TabsContent value="manual">
          <div className="card-surface p-6 max-w-lg">
            <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Add Media by URL</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Universe</Label>
                <Select value={manualUniverse} onValueChange={setManualUniverse}>
                  <SelectTrigger className="input-dark mt-1" data-testid="manual-universe">
                    <SelectValue placeholder="Select universe" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    {universes.map((uni) => (
                      <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Media Type</Label>
                <Select value={manualType} onValueChange={setManualType}>
                  <SelectTrigger className="input-dark mt-1" data-testid="manual-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Video (YouTube)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">URL</Label>
                <Input
                  value={manualUrl}
                  onChange={(e) => setManualUrl(e.target.value)}
                  className="input-dark mt-1"
                  placeholder={manualType === "video" ? "https://www.youtube.com/watch?v=..." : "https://images.unsplash.com/..."}
                  data-testid="manual-url"
                />
              </div>
              <div>
                <Label className="text-slate-300">Title</Label>
                <Input
                  value={manualTitle}
                  onChange={(e) => setManualTitle(e.target.value)}
                  className="input-dark mt-1"
                  placeholder="Image or video title"
                  data-testid="manual-title"
                />
              </div>
              <div>
                <Label className="text-slate-300">Description (optional)</Label>
                <Input
                  value={manualDesc}
                  onChange={(e) => setManualDesc(e.target.value)}
                  className="input-dark mt-1"
                  placeholder="Brief description"
                  data-testid="manual-desc"
                />
              </div>
              <Button onClick={handleManualAdd} className="btn-primary w-full" data-testid="manual-add-btn">
                <Plus className="w-4 h-4 mr-2" /> Add to Gallery
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

const getYouTubeId = (url) => {
  const match = url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : "";
};

export default AdminMediaGallery;
